import { selectActiveCommercialAccounts } from './accountCanonical';

/**
 * Soft operating mode: setup is guidance, not a hard barrier.
 * Provisional AI works whenever accounts exist.
 * Queue readiness requires an explicit founder confirm or defer — never inferred from Tier A count.
 */

export function runwayProceedOk(state, derived) {
  const baselineOk =
    !derived?.runway?.cashUnknown &&
    !derived?.runway?.burnUnknown;
  return baselineOk || !!state?.ui?.runwayUnknownProceed;
}

export function founderQueueOk(state) {
  const ui = state?.ui || {};
  return !!ui.founderQueueConfirmedAt || !!ui.founderQueueDeferred;
}

/** Founder-facing priority queue line — never "N/15". Target band is 10–15. */
export function describePriorityQueue(state, counts = {}) {
  const n = Number(counts.tierA ?? 0);
  const confirmed = founderQueueOk(state);
  if (!confirmed) {
    return n > 0
      ? `${n} provisionally selected · confirmation needed`
      : 'Priority list not confirmed';
  }
  if (n >= 10 && n <= 15) return `${n} priority accounts confirmed · within target range`;
  if (n > 0 && n < 10) return `${n} priority accounts confirmed · below 10–15 target`;
  if (n > 15) return `${n} priority accounts confirmed · above 10–15 target`;
  return 'Priority list confirmed · none selected';}

/**
 * @returns {{
 *   mode: 'setup' | 'ready',
 *   hasAccounts: boolean,
 *   provisionalOk: boolean,
 *   queueOk: boolean,
 *   runwayOk: boolean,
 *   missing: string[],
 *   inferSummary: string,
 * }}
 */
export function deriveOperatingMode(state, derived) {
  const accounts = selectActiveCommercialAccounts(state?.accounts || []);
  const hasAccounts = accounts.length > 0;
  const priorityCount = accounts.filter((a) => a.tier === 'Tier A' || a.tier === 'A').length;
  const proposed = accounts.filter((a) => a.proposedTier === 'Tier A' || a.proposedTier === 'Qualified Target').length;
  const queueOk = founderQueueOk(state);
  const runwayOk = runwayProceedOk(state, derived);
  const missing = [];

  if (!hasAccounts) missing.push('No accounts imported yet.');
  if (hasAccounts && !queueOk) {
    missing.push(
      priorityCount > 0
        ? `Priority list still needs confirmation (${priorityCount} already marked — confirm or defer ~10–15).`
        : `Provisional targets available (${proposed || accounts.length} imported) — confirm the priority account list.`,
    );
  }
  if (!runwayOk) missing.push('Runway baseline not recorded (or mark Unknown and proceed).');

  const mode = queueOk && runwayOk && hasAccounts ? 'ready' : 'setup';
  const inferSummary = !hasAccounts
    ? 'Bridge cannot yet infer commercial priorities — import or sync Dubai accounts.'
    : `Bridge analyzed ${accounts.length} imported account${accounts.length === 1 ? '' : 's'}${
        proposed ? ` · ${proposed} look strong enough for the priority list` : ''
      }${priorityCount ? ` · ${priorityCount} already marked as priority` : ''}.`;

  return {
    mode,
    hasAccounts,
    provisionalOk: hasAccounts,
    queueOk,
    runwayOk,
    missing,
    inferSummary,
    tierA: priorityCount,
    accountCount: accounts.length,
  };
}

/**
 * Adaptive D001 surfacing — three states: Hidden → Candidate → Primary.
 * Primary requires a qualified designated primary opportunity (not stale d001.exists alone).
 */
export function d001Surfacing(derived) {
  const candidates = derived?.funnel?.candidates?.length || 0;
  const primary = !!derived?.funnel?.primary;
  if (primary) {
    return { level: 'primary', showInNav: true, showOnToday: true, showInPipeline: true };
  }
  if (candidates > 0) {
    return { level: 'candidate', showInNav: false, showOnToday: false, showInPipeline: true };
  }
  return { level: 'hidden', showInNav: false, showOnToday: false, showInPipeline: false };
}
